import { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AdminPagination from "@/components/admin/AdminPagination";
import AdminLayout from "../../../components/layout/AdminLayout";
import AdminPageTitle from "@/components/admin/AdminPageTitle";
import { AdminSearchForm, AdminSelectFilter } from "@/components/admin/AdminTableToolbar";
import { App } from "antd";
import {
  type FeedbackStatistics,
  type FeedbackItem,
  type FeedbackSearchParams,
  getStatistics,
  searchFeedback,
  updateInternalNote,
  updateFeedbackDescription,
  exportFeedback,
  parseFeedbackFlags,
} from "../../../services/feedback";

type DateRangeOption = "today" | "month" | "7days" | "30days" | "custom";

interface DropdownOption<T extends string> {
  value: T;
  label: string;
}

interface NoteModalProps {
  feedbackId: string;
  initialNote: string;
  onClose: () => void;
  onSave: (id: string, note: string) => void;
}

function NoteModal({ feedbackId, initialNote, onClose, onSave }: NoteModalProps) {
  const { t } = useTranslation();
  const [note, setNote] = useState(initialNote);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30" onClick={onClose}></div>
      <div className="relative bg-white rounded-xl w-[480px] p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-gray-900">{t("feedback.addNote")}</h3>
          <button
            type="button"
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>
        <div className="mb-1 text-xs text-gray-500">
          {t("feedback.feedbackId")}: {feedbackId}
        </div>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={t("feedback.notePlaceholder")}
          rows={5}
          className="w-full mt-3 px-3 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#830051] focus:border-transparent resize-none"
        />
        <div className="flex justify-end gap-2 mt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={() => {
              onSave(feedbackId, note);
              onClose();
            }}
            className="px-4 py-1.5 bg-[#830051] text-white rounded-lg text-sm font-medium hover:bg-[#6a0042] cursor-pointer whitespace-nowrap"
          >
            {t("feedback.saveNote")}
          </button>
        </div>
      </div>
    </div>
  );
}

function formatDate(dateStr: string): string {
  const d = new Date(new Date(dateStr).toLocaleString());
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatTime(dateStr: string): string {
  const d = new Date(new Date(dateStr).toLocaleString());
  const h = String(d.getHours()).padStart(2, "0");
  const m = String(d.getMinutes()).padStart(2, "0");
  return `${h}:${m}`;
}

/** 根据日期范围选项计算 startTime/endTime */
function computeDateRange(
  range: DateRangeOption,
  startDate: string,
  endDate: string,
): { startTime?: string; endTime?: string } {
  const now = new Date();
  const fmt = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  if (range === "today") {
    const s = fmt(now);
    return { startTime: `${s} 00:00:00`, endTime: `${s} 23:59:59` };
  }
  if (range === "month") {
    const s = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
    return { startTime: `${s} 00:00:00`, endTime: `${fmt(now)} 23:59:59` };
  }
  if (range === "7days") {
    const cutoff = new Date(now);
    cutoff.setDate(now.getDate() - 7);
    return { startTime: `${fmt(cutoff)} 00:00:00`, endTime: `${fmt(now)} 23:59:59` };
  }
  if (range === "30days") {
    const cutoff = new Date(now);
    cutoff.setDate(now.getDate() - 30);
    return { startTime: `${fmt(cutoff)} 00:00:00`, endTime: `${fmt(now)} 23:59:59` };
  }
  if (range === "custom") {
    return {
      startTime: startDate ? `${startDate} 00:00:00` : undefined,
      endTime: endDate ? `${endDate} 23:59:59` : undefined,
    };
  }
  return {};
}

type SortField = "time" | "type";
type SortDirection = "asc" | "desc";

function QuestionTooltip({ text }: { text: string }) {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState<"bottom" | "top">("bottom");
  const containerRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setPos(rect.bottom + 200 > window.innerHeight ? "top" : "bottom");
    }
    setShow(true);
  };

  return (
    <div
      ref={containerRef}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setShow(false)}
    >
      <div className="text-sm text-gray-900 line-clamp-3 leading-snug cursor-default">{text}</div>
      {show && (
        <div
          className={`absolute left-0 z-50 w-72 bg-gray-900 text-white text-xs rounded-lg px-3 py-2.5 leading-relaxed pointer-events-none
            ${pos === "bottom" ? "top-full mt-1.5" : "bottom-full mb-1.5"}`}
        >
          {text}
          <div
            className={`absolute left-4 w-2 h-2 bg-gray-900 rotate-45
              ${pos === "bottom" ? "-top-1" : "-bottom-1"}`}
          />
        </div>
      )}
    </div>
  );
}

function InfoTooltip({ title, content }: { title?: string; content: string }) {
  const [show, setShow] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<"top" | "bottom">("bottom");

  const handleMouseEnter = () => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setPos(rect.bottom + 160 > window.innerHeight ? "top" : "bottom");
    }
    setShow(true);
  };

  return (
    <div
      ref={ref}
      className="relative inline-flex items-center"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setShow(false)}
    >
      <div className="w-4 h-4 flex items-center justify-center cursor-default">
        <i className="ri-information-line text-gray-400 text-base hover:text-gray-600 transition-colors"></i>
      </div>
      {show && (
        <div
          className={`absolute left-1/2 -translate-x-1/2 z-50 w-56 bg-gray-900 text-white text-xs rounded-lg px-3 py-2.5 leading-relaxed pointer-events-none
            ${pos === "bottom" ? "top-full mt-1.5" : "bottom-full mb-1.5"}`}
        >
          {title && <div className="font-semibold mb-1">{title}</div>}
          <div>{content}</div>
          <div
            className={`absolute left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45
              ${pos === "bottom" ? "-top-1" : "-bottom-1"}`}
          />
        </div>
      )}
    </div>
  );
}

interface EditableReasonProps {
  value: string;
  onChange: (v: string) => void;
}

function EditableReason({ value, onChange }: EditableReasonProps) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (editing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
    }
  }, [editing]);

  const handleCommit = () => {
    onChange(draft.trim());
    setEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setDraft(value);
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <textarea
        ref={textareaRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={handleCommit}
        onKeyDown={handleKeyDown}
        rows={3}
        maxLength={1000}
        placeholder={t("feedback.reasonPlaceholder")}
        className="w-full min-w-[160px] px-2 py-1.5 border border-[#830051]/50 rounded-lg text-xs text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#830051] resize-none"
      />
    );
  }

  return (
    <div
      onClick={() => {
        setDraft(value);
        setEditing(true);
      }}
      title={t("feedback.clickToEdit")}
      className="min-w-[160px] min-h-[40px] px-2 py-1.5 rounded-lg border border-transparent hover:border-gray-200 hover:bg-gray-50 cursor-text transition-colors group"
    >
      {value ? (
        <p className="text-xs text-gray-700 line-clamp-3 leading-snug">{value}</p>
      ) : (
        <p className="text-xs text-gray-300 italic group-hover:text-gray-400 transition-colors">
          {t("feedback.reasonPlaceholder")}
        </p>
      )}
    </div>
  );
}

export default function FeedbackPage() {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [filterType, setFilterType] = useState<"all" | "helpful" | "unhelpful">("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [dateRange, setDateRange] = useState<DateRangeOption>("30days");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [noteModal, setNoteModal] = useState<{ id: string; note: string } | null>(null);

  // 服务端数据状态
  const [stats, setStats] = useState<FeedbackStatistics | null>(null);
  const [feedbackData, setFeedbackData] = useState<FeedbackItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [sortField, setSortField] = useState<SortField>("time");
  const [sortDir, setSortDir] = useState<SortDirection>("desc");

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  // 构建搜索参数
  const buildSearchParams = useCallback(
    (p: number): FeedbackSearchParams => {
      const { startTime, endTime } = computeDateRange(dateRange, startDate, endDate);
      const params: FeedbackSearchParams = { Page: p, Limit: limit };
      if (searchQuery) params.keyword = searchQuery;
      if (filterType === "helpful") params.type = "useful";
      else if (filterType === "unhelpful") params.type = "useless";
      if (startTime) params.startTime = startTime;
      if (endTime) params.endTime = endTime;
      return params;
    },
    [searchQuery, filterType, dateRange, startDate, endDate, limit],
  );

  // 加载列表数据
  const fetchList = useCallback(
    async (p: number) => {
      setLoading(true);
      try {
        const res = await searchFeedback(buildSearchParams(p));
        setFeedbackData(res.data.Data ?? []);
        setTotal(res.data.Count ?? 0);
        setPage(res.data.Page ?? p);
      } catch {
        message.error(t("common.loadError"));
      } finally {
        setLoading(false);
      }
    },
    [buildSearchParams, t, message],
  );

  // 加载统计数据
  const fetchStats = useCallback(async () => {
    try {
      const res = await getStatistics();
      setStats(res.data);
    } catch {
      // 统计加载失败不阻断页面
    }
  }, []);

  // 初始加载 & 筛选器变化时重新请求
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    setPage(1);
    fetchList(1);
  }, [fetchList]);

  // 客户端排序（当前页数据）
  const sortedFeedback = [...feedbackData].sort((a, b) => {
    if (sortField === "time") {
      const diff = new Date(a.FeedbackTime).getTime() - new Date(b.FeedbackTime).getTime();
      return sortDir === "asc" ? diff : -diff;
    }
    const aVal = a.Type === "useful" ? 0 : 1;
    const bVal = b.Type === "useful" ? 0 : 1;
    return sortDir === "asc" ? aVal - bVal : bVal - aVal;
  });

  // 统计卡片数据
  const feedbackTotal = stats ? Number(stats.usefulCount) + Number(stats.uselessCount) : 0;

  const handleDateRangeChange = (value: DateRangeOption) => {
    setDateRange(value);
    if (value !== "custom") {
      setStartDate("");
      setEndDate("");
    }
  };

  // 默认时间范围为 30days，筛选或搜索发生变化时显示 Reset 按钮
  const hasActiveFilters =
    searchQuery !== "" ||
    searchInput !== "" ||
    filterType !== "all" ||
    dateRange !== "30days" ||
    startDate !== "" ||
    endDate !== "";

  // 提交搜索：将草稿写入正式搜索状态
  const handleSearch = () => {
    setSearchQuery(searchInput);
  };

  // 重置全部筛选与搜索条件
  const handleReset = () => {
    setSearchQuery("");
    setSearchInput("");
    setFilterType("all");
    setDateRange("30days");
    setStartDate("");
    setEndDate("");
  };

  // 保存内部备注
  const handleSaveNote = async (id: string, note: string) => {
    try {
      await updateInternalNote(id, note);
      setFeedbackData((prev) => prev.map((f) => (f.ID === id ? { ...f, InternalNote: note } : f)));
      message.success(t("feedbackDetail.noteSaved"));
    } catch {
      message.error(t("common.saveError"));
    }
  };

  // 保存反馈意见描述
  const handleSaveDescription = async (id: string, desc: string) => {
    try {
      await updateFeedbackDescription(id, desc);
      setFeedbackData((prev) =>
        prev.map((f) => (f.ID === id ? { ...f, FeedbackDescription: desc } : f)),
      );
    } catch {
      message.error(t("common.saveError"));
    }
  };

  // 导出 Excel
  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await exportFeedback(buildSearchParams(page));
      const blob = new Blob([res.data as unknown as Blob], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // 用本地时区日期命名，避免 toISOString() 按 UTC 导致跨 0 点显示前一天
      const now = new Date();
      const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
      a.download = `feedback_export_${dateStr}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      message.error(t("common.exportError"));
    } finally {
      setExporting(false);
    }
  };

  const feedbackTypeOptions: DropdownOption<"all" | "helpful" | "unhelpful">[] = [
    { value: "all", label: t("feedback.allTypes") },
    { value: "helpful", label: t("common.helpful") },
    { value: "unhelpful", label: t("common.unhelpful") },
  ];

  const dateRangeOptions: DropdownOption<DateRangeOption>[] = [
    { value: "30days", label: t("common.last30Days") },
    { value: "today", label: t("common.today") },
    { value: "month", label: t("common.thisMonth") },
    { value: "7days", label: t("common.last7Days") },
    { value: "custom", label: t("common.customRange") },
  ];

  return (
    <AdminLayout>
      <div>
        {/* Header */}
        <div className="mb-6">
          <AdminPageTitle
            title={
              <>
                {t("feedback.title")}
                <span>({feedbackTotal})</span>
              </>
            }
            tooltip={t("feedback.subtitle")}
          />
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-5 gap-4 mb-6">
          {/* 答案总数 */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="text-xs text-gray-500 mb-2">{t("feedback.totalAnswers")}</div>
            <div
              className="text-2xl font-semibold text-gray-900"
              style={{ color: "rgb(217, 51, 140)" }}
            >
              {stats?.totalAnswers?.toLocaleString() ?? "-"}
            </div>
            <div className="text-xs text-gray-400 mt-1.5">{t("feedback.totalAnswersDesc")}</div>
          </div>

          {/* 反馈数 */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="text-xs text-gray-500 mb-2">{t("feedback.feedbackCount")}</div>
            <div
              className="text-2xl font-semibold text-gray-900"
              style={{ color: "rgb(217, 51, 140)" }}
            >
              {feedbackTotal}
            </div>
            <div className="flex items-center gap-2 mt-1.5">
              <span className="text-xs" style={{ color: "#3CB05C" }}>
                {t("feedback.helpfulShort", { count: stats?.usefulCount ?? 0 })}
              </span>
              <span className="text-gray-300 text-xs">|</span>
              <span className="text-xs" style={{ color: "#FF5050" }}>
                {t("feedback.unhelpfulShort", { count: stats?.uselessCount ?? 0 })}
              </span>
            </div>
          </div>

          {/* 反馈率 */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-1 mb-2">
              <span className="text-xs text-gray-500">{t("feedback.feedbackRate")}</span>
              <InfoTooltip content={t("feedback.feedbackRateTooltip")} />
            </div>
            <div
              className="text-2xl font-semibold text-gray-900"
              style={{ color: "rgb(217, 51, 140)" }}
            >
              {stats?.feedbackRate ?? "-"}%
            </div>
            <div className="text-xs text-gray-400 mt-1.5">{t("feedback.feedbackRateDesc")}</div>
          </div>

          {/* 有用反馈率 */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1">
                <span className="text-xs text-gray-500">{t("feedback.helpfulRate")}</span>
                <InfoTooltip content={t("analytics.helpfulRateTooltipNew")} />
              </div>
              {/* <span
                className="px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap"
                style={{ backgroundColor: "#EDFAF2", color: "#3CB05C" }}
              >
                {t("common.onTarget")}
              </span> */}
            </div>
            <div className="text-2xl font-semibold" style={{ color: "rgb(217, 51, 140)" }}>
              {stats?.usefulRate ?? "-"}%
            </div>
            {/* <div className="text-xs text-gray-500 mt-1">{t("feedback.targetAbove95")}</div> */}
          </div>

          {/* 无用反馈率 */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1">
                <span className="text-xs text-gray-500">{t("feedback.unhelpfulRate")}</span>
                <InfoTooltip content={t("analytics.unhelpfulRateTooltipNew")} />
              </div>
              {parseFloat(stats?.uselessRate) > 2 && (
                <span
                  className="px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap"
                  style={{ backgroundColor: "#FFF0F0", color: "#FF5050" }}
                >
                  {t("common.belowTarget")}
                </span>
              )}
              {parseFloat(stats?.uselessRate) < 2 && (
                <span
                  className="px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap"
                  style={{ backgroundColor: "#EDFAF2", color: "#3CB05C" }}
                >
                  {t("common.onTarget")}
                </span>
              )}
            </div>
            <div className="text-2xl font-semibold" style={{ color: "rgb(217, 51, 140)" }}>
              {stats?.uselessRate ?? "-"}%
            </div>
            <div className="text-xs text-gray-500 mt-1">{t("feedback.targetBelow2")}</div>
          </div>
        </div>

        {/* Filter Row */}
        <div className=" mb-4 flex items-center gap-2 flex-wrap">
          <AdminSelectFilter
            label={t("feedback.filterType")}
            value={filterType}
            onChange={(value) => setFilterType(value as "all" | "helpful" | "unhelpful")}
            options={feedbackTypeOptions}
          />

          <AdminSelectFilter
            label={t("common.dateRange")}
            value={dateRange}
            onChange={(value) => handleDateRangeChange(value as DateRangeOption)}
            options={dateRangeOptions}
          />

          {dateRange === "custom" && (
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="px-2 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-gray-300 cursor-pointer"
              />
              <span className="text-gray-400 text-sm">—</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="px-2 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-gray-300 cursor-pointer"
              />
            </div>
          )}

          <AdminSearchForm
            value={searchInput}
            onChange={setSearchInput}
            onSearch={handleSearch}
            searchLabel={t("common.search")}
            placeholder={t("feedback.searchPlaceholder")}
            showReset={hasActiveFilters}
            onReset={handleReset}
            resetLabel={t("common.reset")}
          />

          <button
            onClick={handleExport}
            disabled={exporting}
            className="ml-auto px-4 py-1.5 bg-[#830051] text-white rounded-lg text-sm font-medium hover:bg-[#6a0042] transition-colors whitespace-nowrap flex items-center gap-1.5 cursor-pointer flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i className={exporting ? "ri-loader-4-line animate-spin" : "ri-download-line"}></i>
            {t("common.export")}
          </button>
        </div>

        {/* Feedback Table */}
        <div className="bg-white border border-gray-100 rounded-xl overflow-x-auto">
          <table className="w-full min-w-[900px] font-sans">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500">
                  <button
                    type="button"
                    onClick={() => handleSort("time")}
                    className="flex items-center gap-1 cursor-pointer hover:text-gray-900 transition-colors whitespace-nowrap group"
                  >
                    {t("feedback.feedbackTime")}
                    <span className="w-3.5 h-3.5 flex flex-col items-center justify-center">
                      <i
                        className={`ri-arrow-up-s-fill text-xs leading-none ${sortField === "time" && sortDir === "asc" ? "text-[#830051]" : "text-gray-300 group-hover:text-gray-400"}`}
                      ></i>
                      <i
                        className={`ri-arrow-down-s-fill text-xs leading-none -mt-1 ${sortField === "time" && sortDir === "desc" ? "text-[#830051]" : "text-gray-300 group-hover:text-gray-400"}`}
                      ></i>
                    </span>
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500">
                  <button
                    type="button"
                    onClick={() => handleSort("type")}
                    className="flex items-center gap-1 cursor-pointer hover:text-gray-900 transition-colors whitespace-nowrap group"
                  >
                    {t("common.type")}
                    <span className="w-3.5 h-3.5 flex flex-col items-center justify-center">
                      <i
                        className={`ri-arrow-up-s-fill text-xs leading-none ${sortField === "type" && sortDir === "asc" ? "text-[#830051]" : "text-gray-300 group-hover:text-gray-400"}`}
                      ></i>
                      <i
                        className={`ri-arrow-down-s-fill text-xs leading-none -mt-1 ${sortField === "type" && sortDir === "desc" ? "text-[#830051]" : "text-gray-300 group-hover:text-gray-400"}`}
                      ></i>
                    </span>
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 max-w-[400px]">
                  {t("feedback.questionTitle")}
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500">
                  {t("feedback.user")}
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500">
                  {t("feedback.sourceDocs")}
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 max-w-[240px]">
                  {t("feedback.reason")}
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 max-w-[120px]">
                  Note
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500">
                  {t("common.actions")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-6 py-16 text-center text-sm text-gray-400">
                    <i className="ri-loader-4-line text-2xl animate-spin inline-block mb-2"></i>
                    <div>{t("common.loading")}</div>
                  </td>
                </tr>
              ) : (
                sortedFeedback.map((feedback) => (
                  <tr key={feedback.ID} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-3.5">
                      <div className="text-sm text-gray-900">
                        {formatDate(feedback.FeedbackTime)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {formatTime(feedback.FeedbackTime)}
                      </div>
                    </td>
                    <td className="px-6 py-3.5">
                      <span
                        className="px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap"
                        style={
                          feedback.Type === "useful"
                            ? { backgroundColor: "#EDFAF2", color: "#3CB05C" }
                            : { backgroundColor: "#FFF0F0", color: "#FF5050" }
                        }
                      >
                        {feedback.Type === "useful" ? t("common.helpful") : t("common.unhelpful")}
                      </span>
                    </td>
                    <td className="px-6 py-3.5 max-w-[400px]">
                      <div className="flex items-center gap-1">
                        <QuestionTooltip text={feedback.Question} />
                        <InfoTooltip
                          title={t("feedback.conversationId")}
                          content={feedback.ConversationID}
                        />
                      </div>
                    </td>
                    <td className="px-6 py-3.5">
                      <div className="text-sm text-gray-700">{feedback.UpdateByName}</div>
                    </td>
                    <td className="px-6 py-3.5">
                      <div className="text-sm text-gray-700">
                        {feedback.SourceDocumentCount ?? 0}
                      </div>
                    </td>
                    <td className="px-6 py-3.5 max-w-[240px]">
                      <div className="flex flex-wrap gap-2 mb-4">
                        {parseFeedbackFlags(feedback.FeedbackFlags).map((flag, idx) => (
                          <span key={idx} className="text-sm text-gray-700">
                            {flag}
                          </span>
                        ))}
                      </div>
                      <div className="text-sm text-gray-700 line-clamp-2">
                        {feedback.FeedbackDescription}
                      </div>
                    </td>
                    <td className="px-6 py-3.5 max-w-[120px]">
                      <div className="text-sm text-gray-700">{feedback.InternalNote}</div>
                    </td>
                    <td className="px-6 py-3.5 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/admin/feedback/${feedback.ID}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1 border border-gray-300 rounded-lg text-xs text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-eye-line text-sm"></i>
                          {t("common.details")}
                        </Link>
                        <button
                          type="button"
                          onClick={() =>
                            setNoteModal({ id: feedback.ID, note: feedback.InternalNote ?? "" })
                          }
                          className="inline-flex items-center gap-1 px-2.5 py-1 border border-gray-300 rounded-lg text-xs text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-edit-line text-sm"></i>
                          {t("common.note")}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {!loading && sortedFeedback.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2">
                <i className="ri-inbox-line text-4xl text-gray-300"></i>
              </div>
              <p className="text-sm">{t("feedback.noRecords")}</p>
            </div>
          )}
        </div>
        <AdminPagination
          total={total}
          page={page}
          pageSize={limit}
          loading={loading}
          onPageChange={(nextPage) => void fetchList(nextPage)}
        />
      </div>

      {noteModal && (
        <NoteModal
          feedbackId={noteModal.id}
          initialNote={noteModal.note}
          onClose={() => setNoteModal(null)}
          onSave={handleSaveNote}
        />
      )}
    </AdminLayout>
  );
}
